import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useRef, useState } from "react";
import { FiArrowLeftCircle, FiArrowRightCircle } from "react-icons/fi";
import { ReviewData } from "./review";

type ReviewCarouselProps = {
    reviews: ReviewData[];
};

export default function ReviewCarousel({ reviews }: ReviewCarouselProps) {
    const reviewTrackRef = useRef<HTMLDivElement>(null);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [reviewTrackSize, setReviewTrackSize] = useState(0);

    const isSingleReview = reviews.length === 1;
    const cardWidth = isSingleReview
        ? reviewTrackSize
        : Math.min(384, reviewTrackSize);
    const gap = 16;
    const step = cardWidth + gap;
    const windowStart = isSingleReview ? currentIndex : currentIndex - 2;
    const windowPositions = isSingleReview
        ? [currentIndex]
        : [
              windowStart,
              currentIndex - 1,
              currentIndex,
              currentIndex + 1,
              currentIndex + 2,
          ];

    const changeIndex = (direction: "forward" | "backward") => {
        setCurrentIndex((prev) => prev + (direction === "forward" ? 1 : -1));
    };

    useEffect(() => {
        const reviewTrack = reviewTrackRef.current;
        if (!reviewTrack) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => {
            setReviewTrackSize(reviewTrack.clientWidth);
        });
        resizeObserver.observe(reviewTrack);
        return () => resizeObserver.disconnect();
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div className="relative w-full max-w-lg">
                {reviews.length > 1 && (
                    <div className="absolute -top-8 right-0 flex gap-2 z-1 text-brand-red">
                        <button
                            aria-label="Previous review"
                            onClick={() => changeIndex("backward")}
                        >
                            <FiArrowLeftCircle size={24} />
                        </button>
                        <button
                            aria-label="Next review"
                            onClick={() => changeIndex("forward")}
                        >
                            <FiArrowRightCircle size={24} />
                        </button>
                    </div>
                )}
                <div
                    ref={reviewTrackRef}
                    className="overflow-hidden rounded-xl"
                >
                    <div
                        data-testid="review-track"
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{
                            transform: `translateX(${-currentIndex * step}px)`,
                        }}
                    >
                        <div
                            className="flex-shrink-0"
                            style={{ marginLeft: windowStart * step }}
                        />
                        {!!reviews.length &&
                            windowPositions.map((position) => {
                                const review =
                                    reviews[
                                        ((position % reviews.length) +
                                            reviews.length) %
                                            reviews.length
                                    ];
                                return (
                                    <div
                                        key={position}
                                        className="mr-4 flex-shrink-0"
                                        style={{
                                            width: cardWidth,
                                            height:
                                                position === currentIndex
                                                    ? "auto"
                                                    : 0,
                                        }}
                                    >
                                        <div
                                            className={`flex flex-col gap-2 rounded-lg bg-brand-lightGray p-4 transition-all ${
                                                position === currentIndex
                                                    ? "scale-100"
                                                    : "scale-95 opacity-60"
                                            }`}
                                        >
                                            <div className="text-brand-black">
                                                {review.text}
                                            </div>
                                            <div className="text-brand-black text-sm font-medium">
                                                – {review.client_name} on{" "}
                                                {formatInTimeZone(
                                                    new Date(review.created_at),
                                                    "America/New_York",
                                                    "MMMM dd, yyyy",
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
}
